import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCw, Settings2, ChevronDown, ChevronUp, ExternalLink,
  ArrowDownRight, ArrowUpRight, CheckCircle2, LineChart, StickyNote, Search as SearchIcon,
} from "lucide-react";

const km = (n) => `${Number(n || 0).toLocaleString()} km`;

function RecBadge({ check }) {
  if (!check) return <span className="text-xs text-muted-foreground">Not scanned</span>;
  const rec = check.recommendation || {};
  if (rec.action === "lower")
    return (
      <Badge className="bg-red-500/12 text-red-400 border-0 gap-1" data-testid="rec-badge-lower">
        <ArrowDownRight className="h-3 w-3" /> Lower to {money(rec.suggested_price)}
      </Badge>
    );
  if (rec.action === "raise")
    return (
      <Badge className="bg-green-500/12 text-green-400 border-0 gap-1" data-testid="rec-badge-raise">
        <ArrowUpRight className="h-3 w-3" /> Raise to {money(rec.suggested_price)}
      </Badge>
    );
  return (
    <Badge className="bg-zinc-500/12 text-zinc-400 border-0 gap-1" data-testid="rec-badge-hold">
      <CheckCircle2 className="h-3 w-3" /> Priced right
    </Badge>
  );
}

function CompsRow({ check }) {
  return (
    <TableRow className="bg-secondary/40 hover:bg-secondary/40">
      <TableCell colSpan={8} className="p-4">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
          Comparable listings ({check.sample_size || check.comps?.length || 0} found) · confidence: {check.confidence}
        </p>
        {check.recommendation?.reason && (
          <p className="text-sm mb-3 max-w-3xl" data-testid="rec-reason">{check.recommendation.reason}</p>
        )}
        <div className="grid gap-1.5">
          {(check.comps || []).map((c, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm bg-card rounded-md border border-border px-3 py-2">
              <span className="font-medium flex-1 min-w-[200px]">{c.title}</span>
              <span className="font-mono-data">{c.price ? money(c.price) : "—"}</span>
              <span className="text-muted-foreground font-mono-data">{c.km ? km(c.km) : "km n/a"}</span>
              <span className="text-xs text-muted-foreground">{c.location}</span>
              <span className="text-xs text-muted-foreground">{c.source}</span>
              {c.url ? (
                <a href={c.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline" title="Open exact listing">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <a
                  href={`https://www.google.com/search?q=${encodeURIComponent(`${c.title || ""} for sale Ontario ${c.source || ""}`)}`}
                  target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground hover:text-foreground" title="Exact link unavailable — search for this listing"
                >
                  <SearchIcon className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ))}
          {(!check.comps || check.comps.length === 0) && (
            <p className="text-sm text-muted-foreground">No comps captured on the last scan.</p>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function SettingsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["price-settings"],
    queryFn: async () => (await api.get("/pricing/settings")).data,
    enabled: open,
  });
  const [form, setForm] = useState(null);
  const s = form || settings || {};
  const set = (k, v) => setForm({ ...s, [k]: v });

  const saveMut = useMutation({
    mutationFn: async () =>
      (await api.put("/pricing/settings", {
        threshold_pct: Number(s.threshold_pct || 5),
        email_alerts: !!s.email_alerts,
        alert_email: s.alert_email || "",
        auto_daily: s.auto_daily !== false,
        dealer_description: s.dealer_description || "",
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-settings"] });
      toast.success("Settings saved");
      onOpenChange(false);
      setForm(null);
    },
    onError: () => toast.error("Failed to save settings"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setForm(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Price Monitor Settings</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="label-mono">About our cars — applies to ALL vehicles</Label>
            <Textarea
              rows={4}
              value={s.dealer_description || ""}
              onChange={(e) => set("dealer_description", e.target.value)}
              placeholder="e.g. Every vehicle we sell comes safetied and certified, fully detailed, with fresh oil change and a 30-day powertrain warranty. Financing available."
              data-testid="pricing-dealer-description-input"
            />
            <p className="text-[11px] text-muted-foreground">Blanket facts the AI treats as true for every car you scan. For details unique to one car (extra options, new tires, warranty left, etc.), use the note icon on that car's row in the matrix.</p>
          </div>
          <div className="space-y-2">
            <Label className="label-mono">Alert threshold (%)</Label>
            <Input type="number" min="1" max="50" value={s.threshold_pct ?? 5} onChange={(e) => set("threshold_pct", e.target.value)} data-testid="pricing-threshold-input" />
            <p className="text-[11px] text-muted-foreground">Notify when the recommended price differs from yours by more than this percentage.</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="label-mono">Daily automatic scan</Label>
              <p className="text-[11px] text-muted-foreground">Runs every morning at 7:00 AM (Toronto).</p>
            </div>
            <Switch checked={s.auto_daily !== false} onCheckedChange={(v) => set("auto_daily", v)} data-testid="pricing-auto-daily-switch" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="label-mono">Email alerts</Label>
              <p className="text-[11px] text-muted-foreground">
                {settings?.email_configured ? "Send a summary email after each scan with alerts." : "Requires RESEND_API_KEY to be configured."}
              </p>
            </div>
            <Switch checked={!!s.email_alerts} disabled={!settings?.email_configured} onCheckedChange={(v) => set("email_alerts", v)} data-testid="pricing-email-alerts-switch" />
          </div>
          {s.email_alerts && (
            <div className="space-y-2">
              <Label className="label-mono">Alert email</Label>
              <Input type="email" value={s.alert_email || ""} onChange={(e) => set("alert_email", e.target.value)} placeholder="you@dealership.com" data-testid="pricing-alert-email-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="pricing-save-settings-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CarDetailsDialog({ vehicle, onOpenChange }) {
  const qc = useQueryClient();
  const [text, setText] = useState(vehicle?.pricing_description || "");

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/pricing/description/${vehicle.id}`, { description: text })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-matrix"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      toast.success("Car details saved — they'll be used on the next scan");
      onOpenChange(false);
    },
    onError: () => toast.error("Failed to save"),
  });

  return (
    <Dialog open={!!vehicle} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Car Details — {vehicle?.year} {vehicle?.make} {vehicle?.model}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="label-mono">Everything this specific car comes with</Label>
          <Textarea
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. New brakes and tires (2025), remaining factory warranty until 2027, winter tire set included, pano roof, one owner, clean Carfax."
            data-testid="car-details-textarea"
          />
          <p className="text-[11px] text-muted-foreground">Saved on this vehicle only. The AI combines it with your "About our cars" facts (which apply to all vehicles) when pricing this car.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="save-car-details-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PriceMatrix() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailsVehicle, setDetailsVehicle] = useState(null);

  const { data: status = {} } = useQuery({
    queryKey: ["price-status"],
    queryFn: async () => (await api.get("/pricing/status")).data,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : 15000),
  });
  const running = !!status.running;

  const { data: matrix = [], isLoading } = useQuery({
    queryKey: ["price-matrix"],
    queryFn: async () => (await api.get("/pricing/matrix")).data,
    refetchInterval: running ? 5000 : false,
  });

  const refreshMut = useMutation({
    mutationFn: async (vehicleId) => (await api.post("/pricing/refresh", vehicleId ? { vehicle_id: vehicleId } : {})).data,
    onSuccess: () => {
      toast.success("Market scan started — results appear as each vehicle completes.");
      qc.invalidateQueries({ queryKey: ["price-status"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start scan"),
  });

  const alertCount = matrix.filter((m) => m.check?.recommendation?.action && m.check.recommendation.action !== "hold").length;

  return (
    <div className="p-8">
      <PageHeader
        title="Price Matrix"
        subtitle="Live Ontario market data via MarketCheck — km, days-on-lot and market-position adjusted."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="pricing-settings-button">
              <Settings2 className="h-4 w-4 mr-2" /> Settings
            </Button>
            <Button onClick={() => refreshMut.mutate(null)} disabled={running || refreshMut.isPending} data-testid="pricing-refresh-all-button">
              <RefreshCw className={`h-4 w-4 mr-2 ${running ? "animate-spin" : ""}`} />
              {running ? `Scanning ${status.done ?? 0}/${status.total ?? 0}…` : "Refresh Market Data"}
            </Button>
          </div>
        }
      />

      {!running && (status.errors || []).length > 0 && (
        <Card className="p-4 mb-4 border-red-500/35 bg-red-500/[0.07]" data-testid="pricing-error-banner">
          <p className="text-sm font-medium text-red-300">Last scan had errors:</p>
          {status.errors.slice(0, 3).map((e, i) => (
            <p key={i} className="text-xs text-red-300 mt-1">{e}</p>
          ))}
          {String(status.errors[0] || "").includes("Budget has been exceeded") && (
            <p className="text-xs text-red-300 font-semibold mt-2">Your Emergent Universal Key balance is used up — go to Profile → Universal Key → Add Balance (or enable auto top-up) to keep scans running.</p>
          )}
        </Card>
      )}

      {alertCount > 0 && (
        <Card className="p-4 mb-4 border-amber-500/35 bg-amber-500/[0.07]" data-testid="pricing-alert-banner">
          <p className="text-sm font-medium text-amber-300">
            <LineChart className="h-4 w-4 inline mr-2" />
            {alertCount} vehicle{alertCount > 1 ? "s" : ""} priced off-market — review the recommendations below.
          </p>
        </Card>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right">My Price</TableHead>
              <TableHead className="text-right">Market Avg</TableHead>
              <TableHead className="text-right">Range</TableHead>
              <TableHead className="text-right">Position</TableHead>
              <TableHead>Recommendation</TableHead>
              <TableHead>Last Scan</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : matrix.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No active inventory. Add vehicles with a listing price to monitor them.</TableCell></TableRow>
            ) : (
              matrix.map((m) => {
                const c = m.check;
                const isOpen = expanded === m.id;
                return [
                  <TableRow key={m.id} data-testid={`price-row-${m.id}`} className={c ? "cursor-pointer" : ""} onClick={() => c && setExpanded(isOpen ? null : m.id)}>
                    <TableCell className="font-semibold">
                      {m.year} {m.make} {m.model}
                      <span className="block text-xs text-muted-foreground font-normal">
                        {m.trim} · {km(m.odometer)} · {m.days_on_lot}d on lot
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono-data">{m.listing_price ? money(m.listing_price) : <span className="text-destructive text-xs">no price</span>}</TableCell>
                    <TableCell className="text-right font-mono-data">{c?.market_avg ? money(c.market_avg) : "—"}</TableCell>
                    <TableCell className="text-right font-mono-data text-xs text-muted-foreground">
                      {c?.market_low ? `${money(c.market_low)} – ${money(c.market_high)}` : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono-data">
                      {c?.position_pct != null ? (
                        <span className={c.position_pct > 0 ? "text-red-400" : "text-green-400"}>
                          {c.position_pct > 0 ? "+" : ""}{c.position_pct}%
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell><RecBadge check={c} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c ? new Date(c.checked_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="icon"
                        onClick={(e) => { e.stopPropagation(); setDetailsVehicle(m); }}
                        data-testid={`car-details-${m.id}`}
                        title={m.pricing_description ? `Car details: ${m.pricing_description}` : "Add details specific to this car"}
                      >
                        <StickyNote className={`h-4 w-4 ${m.pricing_description ? "text-amber-500" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost" size="icon" disabled={running || !m.listing_price}
                        onClick={(e) => { e.stopPropagation(); refreshMut.mutate(m.id); }}
                        data-testid={`price-refresh-${m.id}`} title="Re-scan this vehicle"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      {c && (
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setExpanded(isOpen ? null : m.id); }} data-testid={`price-expand-${m.id}`}>
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>,
                  isOpen && c ? <CompsRow key={`${m.id}-comps`} check={c} /> : null,
                ];
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      {detailsVehicle && <CarDetailsDialog vehicle={detailsVehicle} onOpenChange={() => setDetailsVehicle(null)} />}
    </div>
  );
}
